import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Select, Input, Form, Typography, Tag, Space } from "antd";
import styled from "styled-components";
import tokens from "../../theme/tokens";

const { TextArea } = Input;
const { Text } = Typography;

/**
 * Modal used by:
 *  - The matter document table's "Report incorrect categorization…"
 *    row action — supplies `documentName` and `wrongCategory`, attorney
 *    picks the correct category and writes one sentence about why.
 *  - The Settings > AI Categorization page in "manual" mode — wraps
 *    this modal with a document picker step that produces the same
 *    payload before opening.
 *
 * The modal is intentionally headless re: API calls — it produces a
 * payload via `onSubmit({ actualCategory, reason, notes })` and lets
 * the caller dispatch the slice thunk. That keeps optimistic-cache
 * + toast wiring in one place at the caller.
 */

const ReasonHintRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 4px;
  color: ${tokens.text.tertiary};
  font-size: 12px;
  font-family: ${tokens.typography.fontFamily};
`;

const ReadOnlyValue = styled.div`
  padding: 6px 10px;
  border-radius: 8px;
  background: ${tokens.surface.page};
  border: 1px solid ${tokens.border.light};
  color: ${tokens.text.primary};
  font-family: ${tokens.typography.fontFamily};
  font-size: 13px;
  word-break: break-word;
`;

const REASON_MIN = 20;
const REASON_MAX = 600;

const extractCategoryValue = (category) => {
  if (typeof category === "string") return category;
  if (!category || typeof category !== "object") return "";
  return (
    category.value ||
    category.key ||
    category.code ||
    category.category ||
    category.name ||
    category.label ||
    ""
  );
};

export const buildCategoryOptions = (categories = []) => {
  const source = Array.isArray(categories)
    ? categories
    : categories?.results || categories?.data || [];
  const seen = new Set();
  return source
    .map(extractCategoryValue)
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter(Boolean)
    .filter((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((value) => ({ value, label: value }));
};

const NegativeExampleModal = ({
  open,
  mode = "row-action",
  documentName = "",
  wrongCategory = "",
  categories = [],
  defaultActualCategory = "",
  canEditNotes = false,
  saving = false,
  success = false,
  error = "",
  onCancel,
  onSubmit,
}) => {
  const options = useMemo(() => buildCategoryOptions(categories), [categories]);
  const [actualCategory, setActualCategory] = useState(defaultActualCategory || "");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [touched, setTouched] = useState({ actualCategory: false, reason: false });

  useEffect(() => {
    if (!open) return;
    setActualCategory(defaultActualCategory || "");
    setReason("");
    setNotes("");
    setTouched({ actualCategory: false, reason: false });
  }, [open, defaultActualCategory]);

  const reasonLength = reason.trim().length;
  const reasonValid = reasonLength >= REASON_MIN && reasonLength <= REASON_MAX;
  const categoryValid =
    Boolean(actualCategory) &&
    actualCategory.trim().toLowerCase() !==
      (wrongCategory || "").trim().toLowerCase();
  const canSubmit = categoryValid && reasonValid && !saving && !success;

  const handleSubmit = () => {
    if (!canSubmit) {
      setTouched({ actualCategory: true, reason: true });
      return;
    }
    onSubmit({
      actualCategory,
      reason: reason.trim(),
      notes: canEditNotes ? notes.trim() : undefined,
    });
  };

  const title = mode === "manual" ? "Add negative example" : "Report incorrect categorization";

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={saving ? "Saving…" : success ? "Saved" : "Submit report"}
      cancelText="Cancel"
      okButtonProps={{
        disabled: !canSubmit,
        "data-testid": "negative-example-submit",
      }}
      cancelButtonProps={{ disabled: saving }}
      maskClosable={!saving}
      destroyOnHidden
      data-testid="negative-example-modal"
    >
      <Form layout="vertical" data-testid="negative-example-form">
        <Form.Item label="Document">
          <ReadOnlyValue>{documentName || "—"}</ReadOnlyValue>
        </Form.Item>

        <Form.Item label="What category did the AI assign?">
          <Space>
            {wrongCategory ? (
              <Tag color="orange">{wrongCategory}</Tag>
            ) : (
              <Text type="secondary">No category recorded.</Text>
            )}
          </Space>
        </Form.Item>

        <Form.Item
          label="What's the correct category?"
          required
          validateStatus={
            touched.actualCategory && !categoryValid ? "error" : ""
          }
          help={
            touched.actualCategory && !categoryValid
              ? actualCategory && actualCategory === wrongCategory
                ? "Pick a category different from the AI's choice."
                : "Pick the category the AI should have used."
              : null
          }
        >
          <Select
            placeholder="Select the correct category"
            value={actualCategory || undefined}
            onChange={(value) => {
              setActualCategory(value);
              setTouched((t) => ({ ...t, actualCategory: true }));
            }}
            disabled={saving || success}
            showSearch
            optionFilterProp="label"
            options={options.map((opt) => ({
              ...opt,
              disabled:
                opt.value.trim().toLowerCase() ===
                (wrongCategory || "").trim().toLowerCase(),
            }))}
            data-testid="negative-example-category-select"
          />
        </Form.Item>

        <Form.Item
          label="Why is the AI's choice wrong?"
          required
          validateStatus={touched.reason && !reasonValid ? "error" : ""}
          help={
            touched.reason && !reasonValid
              ? reasonLength < REASON_MIN
                ? `Write at least ${REASON_MIN} characters — short notes don't help the next training run.`
                : `Keep it under ${REASON_MAX} characters.`
              : null
          }
        >
          <TextArea
            rows={4}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              setTouched((t) => ({ ...t, reason: true }));
            }}
            disabled={saving || success}
            placeholder="e.g. This is a C&P Exam report, not a DBQ — DBQs are the structured questionnaire form; this is the examiner's narrative."
            maxLength={REASON_MAX}
            data-testid="negative-example-reason"
          />
          <ReasonHintRow>
            <span>
              {reasonValid
                ? "Looks good."
                : reasonLength === 0
                ? `Minimum ${REASON_MIN} characters.`
                : `${Math.max(REASON_MIN - reasonLength, 0)} more characters needed.`}
            </span>
            <span>
              {reasonLength}/{REASON_MAX}
            </span>
          </ReasonHintRow>
        </Form.Item>

        {canEditNotes && (
          <Form.Item
            label="Internal notes (optional)"
            tooltip="Visible to admins only. Use for context that shouldn't be sent to the AI."
          >
            <TextArea
              rows={2}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              disabled={saving || success}
              placeholder="Optional context for other admins."
              data-testid="negative-example-notes"
            />
          </Form.Item>
        )}

        {error && (
          <Text type="danger" role="alert" data-testid="negative-example-error">
            {error}
          </Text>
        )}
      </Form>
    </Modal>
  );
};

NegativeExampleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["row-action", "manual"]),
  documentName: PropTypes.string,
  wrongCategory: PropTypes.string,
  categories: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape({
      results: PropTypes.array,
      data: PropTypes.array,
      categories: PropTypes.array,
    }),
  ]),
  defaultActualCategory: PropTypes.string,
  canEditNotes: PropTypes.bool,
  saving: PropTypes.bool,
  success: PropTypes.bool,
  error: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default NegativeExampleModal;
